/**
 * useGrants Hook
 *
 * Custom hook for managing grants
 */

import { useState, useEffect, useCallback } from 'react';
import { grantService } from '../services/grantService';
import { sortGrantsByPriority } from '../utils/grantHelpers';

export function useGrants(clientId) {
  const [grants, setGrants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch grants
  // clientId can be null for APP_ADMIN (fetch all grants from all clients)
  // or a specific clientId for CLIENT_ADMIN (fetch only their client's grants)
  const fetchGrants = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const fetchedGrants = await grantService.listGrants(clientId);
      const sorted = sortGrantsByPriority(fetchedGrants);
      setGrants(sorted);
    } catch (err) {
      console.error('Error fetching grants:', err);
      setError(err.message);
      setGrants([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Initial fetch
  useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  // Create grant
  const createGrant = useCallback(async (grantData) => {
    try {
      const newGrant = await grantService.createGrant(grantData, clientId);
      await fetchGrants(); // Refresh list
      return newGrant;
    } catch (err) {
      console.error('Error creating grant:', err);
      throw err;
    }
  }, [clientId, fetchGrants]);

  // Update grant
  const updateGrant = useCallback(async (grantId, updates) => {
    try {
      const updatedGrant = await grantService.updateGrant(grantId, updates, clientId);
      await fetchGrants(); // Refresh list
      return updatedGrant;
    } catch (err) {
      console.error('Error updating grant:', err);
      throw err;
    }
  }, [clientId, fetchGrants]);

  // Delete grant
  const deleteGrant = useCallback(async (grantId) => {
    try {
      const result = await grantService.deleteGrant(grantId, clientId);
      await fetchGrants(); // Refresh list
      return result;
    } catch (err) {
      console.error('Error deleting grant:', err);
      throw err;
    }
  }, [clientId, fetchGrants]);

  // Batch create grants
  const batchCreateGrants = useCallback(async (grantsData) => {
    try {
      const result = await grantService.batchCreateGrants(grantsData, clientId);
      await fetchGrants(); // Refresh list
      return result;
    } catch (err) {
      console.error('Error batch creating grants:', err);
      throw err;
    }
  }, [clientId, fetchGrants]);

  // Batch delete grants
  const batchDeleteGrants = useCallback(async (grantIds) => {
    try {
      const result = await grantService.batchDeleteGrants(grantIds, clientId);
      await fetchGrants(); // Refresh list
      return result;
    } catch (err) {
      console.error('Error batch deleting grants:', err);
      throw err;
    }
  }, [clientId, fetchGrants]);

  // Search grants
  const searchGrants = useCallback(async (searchCriteria) => {
    try {
      setIsLoading(true);
      setError(null);

      const results = await grantService.searchGrants(clientId, searchCriteria);
      return results;
    } catch (err) {
      console.error('Error searching grants:', err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Clone grant
  const cloneGrant = useCallback(async (grantId, newName) => {
    try {
      const cloned = await grantService.cloneGrant(grantId, newName, clientId);
      await fetchGrants(); // Refresh list
      return cloned;
    } catch (err) {
      console.error('Error cloning grant:', err);
      throw err;
    }
  }, [clientId, fetchGrants]);

  // Get grant statistics
  const getStatistics = useCallback(async () => {
    try {
      const stats = await grantService.getGrantStatistics(clientId);
      return stats;
    } catch (err) {
      console.error('Error getting grant statistics:', err);
      throw err;
    }
  }, [clientId]);

  return {
    grants,
    isLoading,
    error,
    refetch: fetchGrants,
    createGrant,
    updateGrant,
    deleteGrant,
    batchCreateGrants,
    batchDeleteGrants,
    searchGrants,
    cloneGrant,
    getStatistics
  };
}